/* eslint-disable no-undef */
/**
 * Deposit Calculator Module
 * Handles calculation logic for savings deposit calculator
 */
export default class DepositCalculator {
  constructor(el) {
    this.$el = $(el);
    this.$form = this.$el.find('[data-calculator]');
    this.$input = this.$form.find('input[name="depositAmount"]');
    this.$resetBtn = this.$el.find('[data-reset]');
    this.$results = this.$el.find('[data-results]');
    this.$displayRate = this.$el.find('[data-display-rate]');
    this.$displayBalance = this.$el.find('[data-display-balance]');
    this.$errorMessage = this.$el.find('[data-error-message]');
    this.$interestRateText = this.$el.find('[data-interest-rate-text]');
    this.$balanceText = this.$el.find('[data-balance-text]');

    // Get tier data from input attributes
    this.tier1Min = parseFloat(this.$input.data('tier1-min')) || 0;
    this.tier1Rate = parseFloat(this.$input.data('tier1-rate')) || 0;
    
    // For tier 2, check if data exists before parsing
    const tier2MinData = this.$input.data('tier2-min');
    const tier2RateData = this.$input.data('tier2-rate');
    
    this.tier2Min = tier2MinData !== undefined && tier2MinData !== '' && tier2MinData !== null ? parseFloat(tier2MinData) : null;
    this.tier2Rate = tier2RateData !== undefined && tier2RateData !== '' && tier2RateData !== null ? parseFloat(tier2RateData) : null;

    // Check if each tier is valid
    const tier1Valid = this.tier1Min > 0 && this.tier1Rate > 0;
    const tier2Valid = this.tier2Min !== null && this.tier2Rate !== null && !isNaN(this.tier2Min) && !isNaN(this.tier2Rate);
    
    // Two-tier mode: enabled only when BOTH tiers are valid
    // Single-tier mode: use whichever tier is available (tier 2 preferred if only one exists)
    this.isTwoTier = tier1Valid && tier2Valid;

    // Get maximum deposit limit from ACF field (optional)
    const maxDepositData = this.$input.data('max-deposit');
    this.maxDeposit = maxDepositData !== undefined && maxDepositData !== '' && maxDepositData !== null ? parseFloat(maxDepositData) : null;

    // Track calculation state
    this.hasCalculated = false;

    // Check if currency formatting is enabled
    this.currencyFormatEnabled = this.$input.data('currency-set') === true;

    // URL params handling
    this.urlParams = new URLSearchParams(window.location.search);
  }

  init() {
    this.bindEvents();
    this.loadFromURL();
    this.updateResetButtonState();
  }

  bindEvents() {
    // Form submit
    this.$form.on('submit', (e) => {
      e.preventDefault();
      this.handleCalculate();
    });

    // Reset button
    this.$resetBtn.on('click', (e) => {
      e.preventDefault();
      this.handleReset();
    });

    // Input change - clear error only
    this.$input.on('input', () => {
      this.clearError();
      
      // Remove non-numeric characters and format currency if enabled
      this.handleInputChange();
    });
  }

  /**
   * Load deposit amount from URL parameter
   */
  loadFromURL() {
    const depositAmount = this.urlParams.get('deposit');
    if (depositAmount && !isNaN(depositAmount)) {
      this.$input.val(depositAmount);
      // Auto-calculate if there's a valid deposit in URL
      setTimeout(() => {
        this.handleCalculate();
      }, 100);
    }
  }

  /**
   * Update URL with current deposit amount
   */
  updateURL(depositAmount) {
    const url = new URL(window.location.href);
    if (depositAmount) {
      url.searchParams.set('deposit', depositAmount);
    } else {
      url.searchParams.delete('deposit');
    }
    window.history.replaceState({}, '', url);
  }

  /**
   * Handle input change - remove letters and format if needed
   */
  handleInputChange() {
    const input = this.$input[0];
    const value = input.value;
    
    // Remove all non-numeric characters (letters, special chars except digits)
    const numericValue = value.replace(/[^\d]/g, '');
    
    // If empty, clear and return
    if (!numericValue) {
      if (value !== '') {
        input.value = '';
      }
      return;
    }
    
    // If currency formatting is enabled, format with commas
    if (this.currencyFormatEnabled) {
      this.formatInputCurrency(numericValue, input);
    } else {
      // Just set the numeric value without formatting
      if (value !== numericValue) {
        const cursorPosition = input.selectionStart;
        input.value = numericValue;
        // Try to maintain cursor position
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    }
  }

  /**
   * Format input value with currency formatting (1,000)
   * @param {string} numericValue - Cleaned numeric string
   * @param {HTMLElement} input - Input element
   */
  formatInputCurrency(numericValue = null, input = null) {
    if (!input) {
      input = this.$input[0];
    }
    
    if (!numericValue) {
      const value = input.value;
      // Remove all non-digit characters
      numericValue = value.replace(/[^\d]/g, '');
    }
    
    // If empty, do nothing
    if (!numericValue) {
      return;
    }
    
    // Format with thousand separators
    const formatted = parseInt(numericValue, 10).toLocaleString('en-GB');
    
    // Get cursor position before update
    const cursorPosition = input.selectionStart;
    const oldValue = input.value;
    
    // Update value
    input.value = formatted;
    
    // Calculate new cursor position
    // Count commas before cursor in old value
    const beforeCursor = oldValue.substring(0, cursorPosition);
    const commasBeforeCursor = (beforeCursor.match(/,/g) || []).length;
    
    // Count commas before same position in new value
    const newBeforeCursor = formatted.substring(0, cursorPosition);
    const newCommasBeforeCursor = (newBeforeCursor.match(/,/g) || []).length;
    
    // Adjust cursor position
    const newCursorPosition = cursorPosition + (newCommasBeforeCursor - commasBeforeCursor);
    
    // Restore cursor position
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  }

  /**
   * Get numeric value from formatted input
   * @returns {number} - Numeric value without formatting
   */
  getNumericInputValue() {
    const value = this.$input.val();
    // Remove all commas and parse
    return parseFloat(value.replace(/,/g, ''));
  }

  /**
   * Enable or disable reset button based on calculation results
   * Only enable when results are displayed
   */
  updateResetButtonState() {
    if (this.hasCalculated) {
      this.$resetBtn.prop('disabled', false).removeClass('disabled');
    } else {
      this.$resetBtn.prop('disabled', true).addClass('disabled');
    }
  }

  /**
   * Calculate the deposit results
   */
  handleCalculate() {
    // Get numeric value (handles formatted input)
    const depositAmount = this.currencyFormatEnabled ? this.getNumericInputValue() : parseFloat(this.$input.val());

    // Validate input
    if (!depositAmount || depositAmount <= 0) {
      this.showError('Please enter a valid deposit amount.');
      this.resetResults();
      return;
    }

    // Determine minimum deposit based on available tiers
    const minimumDeposit = this.getMinimumDeposit();
    
    // Validate minimum deposit
    if (depositAmount < minimumDeposit) {
      const formattedMinimum = minimumDeposit.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.showError(`The entered amount must not be lower than the minimum deposit of this product: \u00A3${formattedMinimum}`);
      this.resetResults();
      return;
    }

    // Validate maximum deposit (if set in ACF)
    if (this.maxDeposit !== null && !isNaN(this.maxDeposit) && depositAmount > this.maxDeposit) {
      const formattedMaximum = this.maxDeposit.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.showError(`The entered amount must not be greater than the maximum deposit of this product: \u00A3${formattedMaximum}`);
      this.resetResults();
      return;
    }

    // Determine applicable rate based on tier logic
    const applicableRate = this.getApplicableRate(depositAmount);

    // Calculate estimated return and balance
    const estimatedReturn = this.calculateReturn(depositAmount, applicableRate);
    const estimatedBalance = depositAmount + estimatedReturn;

    // Display results with tier information
    this.displayResults(applicableRate, estimatedBalance, depositAmount);
  }

  /**
   * Get minimum deposit based on available tiers
   * @returns {number} - Minimum deposit amount
   */
  getMinimumDeposit() {
    // If two-tier mode, use tier 1 minimum
    if (this.isTwoTier) {
      return this.tier1Min;
    }
    
    // Single-tier mode: use whichever tier is available
    // Prefer tier 2 if available, otherwise use tier 1
    const tier2Valid = this.tier2Min !== null && this.tier2Rate !== null && !isNaN(this.tier2Min) && !isNaN(this.tier2Rate);
    if (tier2Valid) {
      return this.tier2Min;
    }
    
    return this.tier1Min;
  }

  /**
   * Get the applicable interest rate based on deposit amount
   * Two-tier logic:
   * - If Deposit ≥ Tier 1 minimum and < Tier 2 minimum → apply Tier 1 AER
   * - If Deposit ≥ Tier 2 minimum → apply Tier 2 AER
   * 
   * Single-tier logic:
   * - Use whichever tier is available (tier 2 preferred if only one exists)
   * - Estimated return = Deposit amount × AER
   * 
   * @param {number} amount - Deposit amount
   * @returns {number} - Applicable rate percentage
   */
  getApplicableRate(amount) {
    if (!this.isTwoTier) {
      // Single-tier product: use whichever tier is available
      // Prefer tier 2 if it exists, otherwise use tier 1
      const tier2Valid = this.tier2Min !== null && this.tier2Rate !== null && !isNaN(this.tier2Min) && !isNaN(this.tier2Rate);
      if (tier2Valid) {
        return this.tier2Rate;
      }
      return this.tier1Rate;
    }

    // Two-tier product: determine applicable AER based on deposit amount and balance tiers
    if (amount >= this.tier2Min) {
      // Deposit meets Tier 2 minimum → apply Tier 2 AER
      return this.tier2Rate;
    } else if (amount >= this.tier1Min) {
      // Deposit meets Tier 1 minimum but below Tier 2 → apply Tier 1 AER
      return this.tier1Rate;
    }

    // Fallback to Tier 1 (should not reach here due to validation)
    return this.tier1Rate;
  }

  /**
   * Calculate estimated return
   * Formula: Deposit amount × AER (rate as percentage)
   * @param {number} deposit - Deposit amount
   * @param {number} rate - Interest rate percentage
   * @returns {number} - Estimated return
   */
  calculateReturn(deposit, rate) {
    return deposit * (rate / 100);
  }

  /**
   * Display calculation results
   * @param {number} rate - Applied interest rate
   * @param {number} balance - Estimated balance after 1 year
   * @param {number} depositAmount - Original deposit amount
   */
  displayResults(rate, balance, depositAmount) {
    // Update rate display
    this.$displayRate.text(`${rate.toFixed(2)}%`);

    // Update balance display (format as currency)
    this.$displayBalance.text(this.formatCurrency(balance));

    // Show result texts
    this.$interestRateText.removeClass('hidden');
    this.$balanceText.removeClass('hidden');

    // Mark as calculated
    this.hasCalculated = true;

    // Enable reset button
    this.updateResetButtonState();

    // Scroll to results
    this.scrollToResults();

    // Push GTM dataLayer event for successful calculation
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'deposit_calc_success',
      calc_rate: `${rate.toFixed(2)}%`,
      calc_balance: this.formatCurrency(balance),
      calc_amount: this.formatCurrency(depositAmount),
      source_page_url: window.location.href
    });
  }

  /**
   * Format number as currency
   * @param {number} amount
   * @returns {string}
   */
  formatCurrency(amount) {
    return `\u00A3${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  /**
   * Show error message
   * @param {string} message - Custom error message (optional)
   */
  showError(message = null) {
    if (message) {
      this.$errorMessage.text(message);
    }
    this.$errorMessage.removeClass('hidden');
    this.$input.addClass('error');
  }

  /**
   * Clear error message
   */
  clearError() {
    this.$errorMessage.addClass('hidden');
    this.$input.removeClass('error');
  }

  /**
   * Reset result values to default
   */
  resetResults() {
    // Reset displayed values
    this.$displayRate.text('xx%');
    this.$displayBalance.text('\u00A30');
    
    // Hide result texts
    this.$interestRateText.addClass('hidden');
    this.$balanceText.addClass('hidden');
    
    // Mark as not calculated
    this.hasCalculated = false;
  }

  /**
   * Reset calculator
   */
  handleReset() {
    // Clear input
    this.$input.val('');

    // Reset results
    this.resetResults();

    // Disable reset button
    this.updateResetButtonState();

    // Clear error
    this.clearError();

    // Focus on input
    this.$input.focus();
  }

  /**
   * Scroll to results section
   */
  scrollToResults() {
    if (this.$results.length) {
      $('html, body').animate(
        {
          scrollTop: this.$results.offset().top - 250,
        },
        300
      );
    }
  }

  /**
   * Cleanup
   */
  destroy() {
    this.$form.off('submit');
    this.$resetBtn.off('click');
    this.$input.off('input');
  }
}

// ============================================
// INITIALIZATION
// ============================================
const INIT_CONFIG = {
  BLOCK_SELECTOR: '.gg-deposit-calculator',
  ACF_BLOCK_TYPE: 'render_block_preview/type=acf/gravity-deposit-calculator',
};

// Initialize on page load
if (typeof window.acf === 'undefined') {
  $(document).ready(() => {
    $(INIT_CONFIG.BLOCK_SELECTOR).each((_index, el) => {
      new DepositCalculator(el).init();
    });
  });
} else {
  acf.addAction(INIT_CONFIG.ACF_BLOCK_TYPE, ($el) => {
    $el.find(INIT_CONFIG.BLOCK_SELECTOR).each((_index, el) => {
      new DepositCalculator(el).init();
    });
  });
}
